import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  useGetAllVideosQuery,
  useDeleteVideoMutation,
  useUpdateVideoMutation,
} from "../../services/modules/video";
import { useGetAllCategoriesQuery } from "../../services/modules/category";
import { useGetAllNetworksQuery } from "../../services/modules/network";

import { FaWrench,FaTrash,FaPlay } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch,useSelector } from "react-redux";
import {
  setSearchTerm,
  openDeleteModal,
  closeDeleteModal,
  openPreviewModal,
  closePreviewModal,
  openEditModal,
  closeEditModal,
  updateEditField,
} from "../../store/video";

const VideoList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    searchTerm,
    isDeleteModalOpen,
    selectedVideoId,
    isPreviewModalOpen,
    previewUrl,
    selectedVideo,
    isEditModalOpen,
    editVideoData,
  } = useSelector((state) => state.video);

  const searchValue = (searchTerm || "").trim();

  const [page,setPage] = React.useState(1);
  const [pageSize,setPageSize] = React.useState(20);

  const {
    data,
    isLoading,
    isFetching,
    refetch
  } = useGetAllVideosQuery(
    {
      page,
      pageSize,
      title: searchValue,
    }
  );

  const responseData = data?.data || data || {};
  const pagination = React.useMemo(() => {
    if (responseData?.pagination) {
      return responseData.pagination;
    }

    const fallbackTotal = Array.isArray(responseData?.result)
      ? responseData.result.length
      : 0;

    return {
      page,
      pageSize,
      total: fallbackTotal,
      totalPages: fallbackTotal ? Math.max(1, Math.ceil(fallbackTotal / pageSize)) : 1,
    };
  }, [responseData, page, pageSize]);

  const totalPages = pagination?.totalPages || 1;
  const totalItems = pagination?.total || 0;

  const { data: categoriesData } = useGetAllCategoriesQuery();
  const { data: networksData } = useGetAllNetworksQuery();

  const [deleteVideo] = useDeleteVideoMutation();
  const [updateVideo] = useUpdateVideoMutation();

  const videos = (responseData?.result || []).filter(
    (video) => video?.id && video?.title
  );

  const filteredVideos = videos.filter((video) => {
    const searchContent = `
      ${video.title || ""}      
      ${video.type || ""}
      ${video.description || ""}
      ${video.categories?.[0]?.name || ""}
    `.toLowerCase();
    return searchContent.includes(searchValue.toLowerCase());
  });

  const currentPage = pagination?.page || page;
  const effectivePageSize = pagination?.pageSize || pageSize;
  const firstItemIndex = totalItems
    ? (currentPage - 1) * effectivePageSize + 1
    : 0;
  const lastItemIndex = totalItems
    ? Math.min(currentPage * effectivePageSize, totalItems)
    : 0;

  React.useEffect(() => {
    setPage(1);
  }, [searchValue]);

  React.useEffect(() => {
    if (totalPages && page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setPage(currentPage - 1);
    }
  };

  const handlePageSizeChange = (event) => {
    const nextSize = Number(event.target.value) || 20;
    setPageSize(nextSize);
    setPage(1);
  };

  const getVimeoEmbedUrl = (url) => {
    if (!url) return null;

    const cleanUrl = url.replace(/&amp;/g,"&");
    // 1° gruppo = videoId, 2° gruppo = hash (opzionale)
    const regex =
      /(?:player\.)?vimeo\.com\/(?:channels\/[\w]+\/)?(?:video\/)?(\d+)(?:\?h=([\w\d]+))?/;
    const match = cleanUrl.match(regex);

    if (match) {
      const videoId = match[1];
      const hash = match[2] || null;
      const embedUrl = hash
        ? `https://player.vimeo.com/video/${videoId}?h=${hash}`
        : `https://player.vimeo.com/video/${videoId}`;

      return embedUrl;
    }

    return null;
  };

  // Helpers per YouTube
  const getYouTubeId = (url) => {
    if (!url) return null;
    try {
      const cleaned = String(url).trim().replace(/&amp;/g, "&");
      try {
        const u = new URL(cleaned);
        const idByV = u.searchParams.get("v");
        if (idByV && idByV.length >= 11) return idByV.substring(0, 11);
      } catch {
        // ignore URL parse error
      }
      const patterns = [
        /youtu\.be\/([\w-]{11})/i,
        /youtube\.com\/embed\/([\w-]{11})/i,
        /youtube\.com\/shorts\/([\w-]{11})/i,
        /v=([\w-]{11})/i,
      ];
      for (const re of patterns) {
        const m = cleaned.match(re);
        if (m && m[1]) return m[1];
      }
      return null;
    } catch (_) {
      return null;
    }
  };

  const getYouTubeEmbedUrl = (url, { autoplay = true, mute = true } = {}) => {
    const id = getYouTubeId(url);
    if (!id) return null;
    const params = new URLSearchParams();
    if (autoplay) params.set("autoplay", "1");
    if (mute) params.set("mute", "1");
    params.set("rel", "0");
    return `https://www.youtube.com/embed/${id}?${params.toString()}`;
  };

  const confirmDelete = async () => {
    try {
      await deleteVideo(selectedVideoId).unwrap();
      toast.success("Video cancellato con successo!");
      dispatch(closeDeleteModal());
      refetch();
    } catch (error) {
      console.error("Delete Error:",error);
      toast.error("Salvataggio non riuscito. Riprova.");
      dispatch(closeDeleteModal());
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        id: editVideoData.id,
        title: editVideoData.title,
        description: editVideoData.description,
        thumbnail: editVideoData.thumbnail,
        categoryIds: [Number(editVideoData.categoryId)],
        networkIds: editVideoData.networkIds,
      };

      console.log("Saving video...");
      console.log("Title:",payload.title);
      console.log("Category ID:",payload.categoryId);
      console.log("handleUpdate:",payload);
      await updateVideo(payload).unwrap();

      toast.success("Video updated successfully!");

      dispatch(closeEditModal());
    } catch (error) {
      console.error("Update Error:",error);
      toast.error("Update failed. Please try again.");
    }
  };

  return (
    <div className="p-6 relative">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold">Lista Video</h2>
        <button
          onClick={() => navigate("/admin/uploadVideo")}
          className="bg-orange-500 text-white px-4 py-2 rounded-full shadow-lg hover:bg-orange-600"
          title="Click add new video"
        >
          +
        </button>
      </div>
      <div className="w-[28%] mb-4">
        <input
          type="text"
          placeholder="Cerca in tutti i campi..."
          value={searchTerm}
          onChange={(e) => dispatch(setSearchTerm(e.target.value))}
          className="border p-2 w-full rounded"
        />
      </div>
      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="w-full text-base text-left bg-white">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3">Thumbnail</th>
              <th className="p-3">Titolo</th>
              <th className="p-3">Tipo</th>
              <th className="p-3">Descrizione</th>
              <th className="p-3">Categoria</th>
              <th className="p-3 text-center">Azione</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && !Array.isArray(responseData?.result) ? (
              <tr>
                <td colSpan="6" className="p-4 text-center">
                  Sto caricando...
                </td>
              </tr>
            ) : filteredVideos.length > 0 ? (
              filteredVideos.map((video) => (
                <tr key={video.id} className="border-t">
                  <td className="p-3">
                    <img
                      src={
                        video.thumbnail || "https://via.placeholder.com/80x45"
                      }
                      alt="thumb"
                      className="w-20 h-auto rounded"
                    />
                  </td>
                  <td className="p-3">{video.title}</td>
                  <td className="p-3">{video.type}</td>
                  <td className="p-3">{video.description}</td>
                  <td className="p-3">
                    {categoriesData?.data?.result?.find(
                      (c) => c.id === video.categories?.[0]?.id
                    )?.name || "-"}
                  </td>
                  <td className="p-3 flex gap-4 justify-evenly mt-5">
                    <button
                      title="Edit"
                      onClick={() => dispatch(openEditModal(video))}
                      className="hover:text-blue-500"
                    >
                      <FaWrench />
                    </button>
                    <button
                      title="Preview"
                      onClick={() => dispatch(openPreviewModal(video))}
                      className="hover:text-green-500"
                    >
                      <FaPlay />
                    </button>
                    <button
                      title="Delete"
                      onClick={() => dispatch(openDeleteModal(video.id))}
                      className="hover:text-red-500"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="p-4 text-center text-gray-500">
                  No videos found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Elementi per pagina</span>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className="border rounded px-2 py-1"
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage <= 1}
            className={`px-3 py-1 rounded border ${
              currentPage <= 1
                ? "text-gray-400 border-gray-200 cursor-not-allowed"
                : "text-gray-700 border-gray-300 hover:bg-gray-100"
            }`}
          >
            Precedente
          </button>
          <span className="text-sm text-gray-600">
            Pagina {currentPage} di {totalPages || 1}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage >= totalPages}
            className={`px-3 py-1 rounded border ${
              currentPage >= totalPages
                ? "text-gray-400 border-gray-200 cursor-not-allowed"
                : "text-gray-700 border-gray-300 hover:bg-gray-100"
            }`}
          >
            Successiva
          </button>
        </div>
      </div>

      <div className="mt-2 text-sm text-gray-500">
        {totalItems > 0 ? (
          <>
            Mostrando {firstItemIndex}-{lastItemIndex} di {totalItems} video
          </>
        ) : (
          "Nessun video disponibile"
        )}
        {isFetching && (
          <span className="ml-2 text-gray-400">Aggiornamento in corso...</span>
        )}
      </div>

      {isDeleteModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-md w-96 text-center">
            <h3 className="text-lg font-bold mb-4">Conferma Eliminazione</h3>
            <p>Sei sicuro che vuoi cancellare questo video?</p>
            <div className="mt-6 flex justify-center gap-4">
              <button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
              >
                Yes, Delete
              </button>
              <button
                onClick={() => dispatch(closeDeleteModal())}
                className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isPreviewModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90"
          onClick={() => dispatch(closePreviewModal())}
        >
          <div
            className="relative w-screen h-screen flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={() => dispatch(closePreviewModal())}
              className="absolute top-4 right-4 text-white text-3xl leading-none"
              aria-label="Chiudi"
            >
              &times;
            </button>

            {/* Titolo centrato sopra al video */}
            <div className="text-center text-white text-lg md:text-2xl font-bold py-4">
              {selectedVideo?.title || "Video Preview"}
            </div>

            {/* Player full height meno l’altezza del titolo */}
            <div className="flex-grow flex items-center justify-center">
              <div className="w-full h-full">
                {String(previewUrl || "").toLowerCase().includes("vimeo.com") ? (
                  <iframe
                    src={getVimeoEmbedUrl(previewUrl) || ""}
                    title={selectedVideo?.title || "Vimeo Player"}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                ) : getYouTubeEmbedUrl(previewUrl) ? (
                  <iframe
                    src={getYouTubeEmbedUrl(previewUrl)}
                    title={selectedVideo?.title || "YouTube Player"}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video src={previewUrl} controls className="w-full h-full" />
                )}
              </div>
            </div>
          </div>
        </div>

      )}

      {isEditModalOpen && editVideoData && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-xl relative">
            <button
              onClick={() => dispatch(closeEditModal())}
              className="absolute top-2 right-2 text-gray-600 hover:text-black text-xl"
            >
              &times;
            </button>
            <h3 className="text-lg font-bold mb-6 text-center">
              Modifica Video
            </h3>
            <form onSubmit={handleUpdate} className="space-y-4">
              {/* Title */}
              <input
                type="text"
                value={editVideoData.title}
                onChange={(e) =>
                  dispatch(
                    updateEditField({ field: "title",value: e.target.value })
                  )
                }
                className="border p-2 w-full rounded"
                placeholder="Video Title"
                required
              />

              {/* Thumbnail URL */}
              <div>
                <label className="block text-sm font-medium mb-1">Thumbnail URL</label>
                <input
                  type="url"
                  value={editVideoData.thumbnail || ""}
                  onChange={(e) =>
                    dispatch(
                      updateEditField({ field: "thumbnail", value: e.target.value })
                    )
                  }
                  className="border p-2 w-full rounded"
                  placeholder="https://.../image.jpg"
                />
                {editVideoData.thumbnail && (
                  <div className="mt-2 flex justify-center">
                    <img
                      src={editVideoData.thumbnail}
                      onError={(e) => (e.currentTarget.src = "/fallback-thumbnail.jpg")}
                      alt="Anteprima thumbnail"
                      className="w-40 h-auto rounded shadow"
                    />
                  </div>
                )}
              </div>

              {/* Description */}
              <textarea
                value={editVideoData.description}
                onChange={(e) =>
                  dispatch(
                    updateEditField({
                      field: "description",
                      value: e.target.value,
                    })
                  )
                }
                className="border p-2 w-full rounded"
                placeholder="Video Description"
                rows={3}
              />

              {/* Category Select */}
              <select
                value={String(editVideoData.categoryId || "")}
                onChange={(e) => {
                  const selectedId = Number(e.target.value);
                  const selectedName =
                    categoriesData?.data?.result?.find(
                      (c) => c.id === selectedId
                    )?.name || "";
                  dispatch(
                    updateEditField({ field: "categoryId",value: selectedId })
                  );
                  dispatch(
                    updateEditField({
                      field: "categoryName",
                      value: selectedName,
                    })
                  );
                }}
                className="border p-2 w-full rounded"
                required
              >
                <option value="">Seleziona Categoria</option>
                {categoriesData?.data?.result?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>

              {/* Network Select */}
              <select
                multiple
                name="networkIds"
                value={editVideoData.networkIds || []}
                onChange={e => {
                  const selectedIds = Array.from(e.target.selectedOptions,opt => Number(opt.value));
                  dispatch(
                    updateEditField({
                      field: "networkIds",
                      value: selectedIds,
                    })
                  );
                }}
                className="border p-2 w-full rounded h-40 border-red-300"
                required
              >
                {networksData?.data?.result?.map(net => (
                  <option key={net.id} value={net.id}>
                    {net.name}
                  </option>
                ))}
              </select>

              {editVideoData.networkIds?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {editVideoData.networkIds.map(id => {
                    const net = networksData?.data?.result?.find(n => n.id === id);
                    return (
                      <span
                        key={id}
                        className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-base"
                      >
                        {net?.name || `ID ${id}`}
                      </span>
                    );
                  })}
                </div>
              )}



              {/* Save Button */}
              <button
                type="submit"
                className="bg-black hover:bg-gray-700 text-white px-4 py-2 rounded w-full"
              >
                Salva Modifiche
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoList;
